/**
 * Printable QR sticker rendering for Bilt build profiles.
 *
 * Vinyl templates (2in kit, square sticker, windshield) stay scan-safe:
 * black modules on white with a quiet zone. Accent color is applied only to
 * chrome (bars, rules, lockup tint) — never to the QR modules.
 *
 * Server-generated QRs already embed the Bilt badge + build URL strip.
 * Stickers crop to the module square and reprint the URL in the same format.
 */
import {
  BILT_MARK_MONO_SRC,
  formatBiltBuildUrlLabel,
} from './biltBrandDisplay';

export type QrStickerTemplate = 'square_2in' | 'square_sticker' | 'windshield' | 'event_card' | 'show_board';

/** @deprecated Prefer importing from biltBrandDisplay. Kept for existing callers. */
export { BILT_MARK_MONO_SRC };

export interface QrStickerTemplateSpec {
  width: number;
  height: number;
  label: string;
  /** Short use-case caption for pickers. */
  useCase: string;
  /** Physical print size at 300 DPI. */
  physicalSize: string;
  /** Whether the template can include a vehicle photo. */
  supportsVehicleImage: boolean;
  /** Light vinyl (single-ink friendly) vs dark display board. */
  surface: 'vinyl' | 'display';
}

export const QR_STICKER_TEMPLATES: Record<QrStickerTemplate, QrStickerTemplateSpec> = {
  square_2in: {
    width: 900,
    height: 900,
    label: '2-inch QR kit',
    useCase: '2in square print kit',
    physicalSize: '2 x 2 in',
    supportsVehicleImage: false,
    surface: 'vinyl',
  },
  square_sticker: {
    width: 1200,
    height: 1200,
    label: 'Square sticker',
    useCase: '4in square sticker',
    physicalSize: '4 x 4 in',
    supportsVehicleImage: false,
    surface: 'vinyl',
  },
  windshield: {
    width: 1800,
    height: 600,
    label: 'Windshield strip',
    useCase: 'Windshield strip',
    physicalSize: '6 x 2 in',
    supportsVehicleImage: false,
    surface: 'vinyl',
  },
  event_card: {
    width: 1500,
    height: 2100,
    label: 'Event card',
    useCase: '5x7 event card',
    physicalSize: '5 x 7 in',
    supportsVehicleImage: true,
    surface: 'display',
  },
  show_board: {
    width: 3300,
    height: 2550,
    label: 'Show board',
    useCase: 'Show board 11x8.5',
    physicalSize: '11 x 8.5 in',
    supportsVehicleImage: true,
    surface: 'display',
  },
};

export interface QrStickerOptions {
  template: QrStickerTemplate;
  vehicleName: string;
  /** Short display URL, e.g. "biltspec.com/user/slug". */
  shortUrl: string;
  /** Already-loaded QR image element (from the app's existing QR source). */
  qrImage: HTMLImageElement;
  /** Monochrome Bilt B-mark (black on transparent), for vinyl lockups. */
  brandMark?: HTMLImageElement | null;
  /** Approved accent hex color; omit for the neutral treatment. */
  accentColor?: string | null;
  /** Only used on event_card and show_board templates. */
  vehicleImage?: HTMLImageElement | null;
}

const FONT_STACK = "-apple-system, 'Helvetica Neue', Arial, sans-serif";

/** Dark display templates (event card / show board). */
const COLORS = {
  bg: '#09090b',
  text: '#fafafa',
  muted: '#a1a1aa',
  dim: '#71717a',
  border: 'rgba(255,255,255,0.14)',
};

/** Single-ink vinyl / decal templates. */
const VINYL = {
  bg: '#ffffff',
  ink: '#000000',
  muted: '#222222',
  hairline: '#000000',
};

function font(weight: number | string, size: number): string {
  return `${weight} ${Math.round(size)}px ${FONT_STACK}`;
}

function truncate(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let out = text;
  while (out.length > 1 && ctx.measureText(`${out}…`).width > maxWidth) {
    out = out.slice(0, -1);
  }
  return `${out}…`;
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number
): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';

  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
      if (lines.length >= maxLines) {
        current = '';
        break;
      }
    } else {
      current = test;
    }
  }
  if (current && lines.length < maxLines) lines.push(current);
  if (lines.length === maxLines && (current || words.length > 0)) {
    const last = lines[lines.length - 1];
    lines[lines.length - 1] = truncate(ctx, last, maxWidth);
  }
  return lines.length > 0 ? lines : [truncate(ctx, text, maxWidth)];
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function drawImageCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number
) {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) return;
  const scale = Math.max(w / iw, h / ih);
  const sw = w / scale;
  const sh = h / scale;
  const sx = (iw - sw) * 0.5;
  const sy = (ih - sh) * 0.4;
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

/**
 * Draw only the square module area from a branded QR (which may include a
 * URL strip below the modules). Avoids squashing the label into the square.
 */
function drawQrModules(
  ctx: CanvasRenderingContext2D,
  qr: HTMLImageElement,
  dx: number,
  dy: number,
  size: number
) {
  const srcW = qr.naturalWidth || size;
  const srcH = qr.naturalHeight || size;
  const srcSize = Math.min(srcW, srcH);
  ctx.drawImage(qr, 0, 0, srcSize, srcSize, dx, dy, size, size);
}

function drawQrPanel(
  ctx: CanvasRenderingContext2D,
  qr: HTMLImageElement,
  x: number,
  y: number,
  qrSize: number
): number {
  const quiet = Math.max(24, Math.round(qrSize * 0.1));
  const panel = qrSize + quiet * 2;
  ctx.fillStyle = '#ffffff';
  roundRectPath(ctx, x, y, panel, panel, Math.round(quiet * 0.75));
  ctx.fill();
  drawQrModules(ctx, qr, x + quiet, y + quiet, qrSize);
  return panel;
}

function qrPanelSize(qrSize: number): number {
  const quiet = Math.max(24, Math.round(qrSize * 0.1));
  return qrSize + quiet * 2;
}

function resolveAccent(accent: string | null | undefined, fallback: string): string {
  return accent && /^#[0-9a-fA-F]{6}$/.test(accent) ? accent : fallback;
}

/** Top/bottom accent chrome. Never painted into the QR quiet zone. */
function drawAccentChrome(
  ctx: CanvasRenderingContext2D,
  accent: string | null | undefined,
  width: number,
  height: number,
  barH: number,
  mode: 'vinyl' | 'display'
) {
  if (!accent) {
    if (mode === 'display') {
      ctx.fillStyle = COLORS.border;
      ctx.fillRect(0, 0, width, barH);
    }
    return;
  }
  const color = resolveAccent(accent, mode === 'vinyl' ? VINYL.ink : COLORS.border);
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, width, barH);
  ctx.fillRect(0, height - Math.max(6, Math.round(barH * 0.55)), width, Math.max(6, Math.round(barH * 0.55)));
}

function drawAccentRule(
  ctx: CanvasRenderingContext2D,
  accent: string | null | undefined,
  x: number,
  y: number,
  w: number,
  h: number,
  fallback: string
) {
  ctx.fillStyle = resolveAccent(accent, fallback);
  ctx.fillRect(x, y, w, h);
}

function drawBiltMark(
  ctx: CanvasRenderingContext2D,
  mark: HTMLImageElement | null | undefined,
  x: number,
  y: number,
  size: number,
  ink = VINYL.ink
) {
  if (!mark || mark.naturalWidth <= 0) return;
  if (ink === VINYL.ink || ink === '#000000') {
    ctx.drawImage(mark, x, y, size, size);
    return;
  }
  const off = document.createElement('canvas');
  off.width = size;
  off.height = size;
  const octx = off.getContext('2d');
  if (!octx) {
    ctx.drawImage(mark, x, y, size, size);
    return;
  }
  octx.fillStyle = ink;
  octx.fillRect(0, 0, size, size);
  octx.globalCompositeOperation = 'destination-in';
  octx.drawImage(mark, 0, 0, size, size);
  ctx.drawImage(off, x, y);
}

function drawBrandLockup(
  ctx: CanvasRenderingContext2D,
  mark: HTMLImageElement | null | undefined,
  cx: number,
  y: number,
  markSize: number,
  textSize: number,
  ink = VINYL.ink
) {
  ctx.save();
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.font = font(700, textSize);
  const label = 'BILT';
  const gap = Math.round(markSize * 0.22);
  const textW = ctx.measureText(label).width;
  const total = (mark && mark.naturalWidth > 0 ? markSize + gap : 0) + textW;
  const left = cx - total / 2;
  let textX = left;
  if (mark && mark.naturalWidth > 0) {
    drawBiltMark(ctx, mark, left, y - markSize / 2, markSize, ink);
    textX = left + markSize + gap;
  }
  ctx.fillStyle = ink;
  ctx.fillText(label, textX, y);
  ctx.restore();
}

function drawUrlBlock(
  ctx: CanvasRenderingContext2D,
  options: QrStickerOptions,
  cx: number,
  y: number,
  maxWidth: number,
  cueSize: number,
  urlSize: number,
  ink: string,
  muted: string,
  align: CanvasTextAlign = 'center'
) {
  const urlLabel = formatBiltBuildUrlLabel(options.shortUrl);
  ctx.textAlign = align;
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = muted;
  ctx.font = font(500, cueSize);
  ctx.fillText('Scan for the full build', cx, y);
  ctx.fillStyle = resolveAccent(options.accentColor, ink);
  ctx.font = font(600, urlSize);
  const drawn = truncate(ctx, urlLabel, maxWidth);
  ctx.fillText(drawn, cx, y + Math.round(urlSize * 1.55));

  // Accent underline under the URL for vinyl/display identity.
  if (options.accentColor) {
    const tw = ctx.measureText(drawn).width;
    const ux = align === 'left' ? cx : cx - tw / 2;
    drawAccentRule(
      ctx,
      options.accentColor,
      ux,
      y + Math.round(urlSize * 1.55) + 10,
      tw,
      Math.max(3, Math.round(urlSize * 0.08)),
      ink
    );
  }
}

function drawSquare2In(ctx: CanvasRenderingContext2D, options: QrStickerOptions) {
  const { width, height } = QR_STICKER_TEMPLATES.square_2in;
  ctx.fillStyle = VINYL.bg;
  ctx.fillRect(0, 0, width, height);
  drawAccentChrome(ctx, options.accentColor, width, height, 14, 'vinyl');

  ctx.strokeStyle = VINYL.hairline;
  ctx.lineWidth = 4;
  ctx.strokeRect(18, 18, width - 36, height - 36);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  const lockupInk = resolveAccent(options.accentColor, VINYL.ink);
  drawBrandLockup(ctx, options.brandMark, width / 2, 78, 36, 34, lockupInk);

  ctx.fillStyle = VINYL.ink;
  ctx.font = font(700, 40);
  const nameLines = wrapText(ctx, options.vehicleName, width - 100, 2);
  let y = nameLines.length > 1 ? 134 : 146;
  for (const line of nameLines) {
    ctx.fillText(line, width / 2, y);
    y += 46;
  }

  const qrSize = 440;
  const panel = qrPanelSize(qrSize);
  const quiet = Math.max(24, Math.round(qrSize * 0.1));
  const panelX = (width - panel) / 2;
  const panelY = y + 4;
  drawQrModules(ctx, options.qrImage, panelX + quiet, panelY + quiet, qrSize);

  drawUrlBlock(
    ctx,
    options,
    width / 2,
    panelY + panel + 36,
    width - 100,
    22,
    24,
    VINYL.ink,
    VINYL.muted
  );

  drawBrandLockup(ctx, options.brandMark, width / 2, height - 52, 28, 26, VINYL.ink);
}

function drawSquareSticker(ctx: CanvasRenderingContext2D, options: QrStickerOptions) {
  const { width, height } = QR_STICKER_TEMPLATES.square_sticker;
  ctx.fillStyle = VINYL.bg;
  ctx.fillRect(0, 0, width, height);
  drawAccentChrome(ctx, options.accentColor, width, height, 18, 'vinyl');

  ctx.strokeStyle = VINYL.hairline;
  ctx.lineWidth = 6;
  ctx.strokeRect(28, 28, width - 56, height - 56);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  const lockupInk = resolveAccent(options.accentColor, VINYL.ink);
  drawBrandLockup(ctx, options.brandMark, width / 2, 108, 48, 44, lockupInk);

  ctx.fillStyle = VINYL.ink;
  ctx.font = font(700, 52);
  const nameLines = wrapText(ctx, options.vehicleName, width - 160, 2);
  let y = nameLines.length > 1 ? 184 : 204;
  for (const line of nameLines) {
    ctx.fillText(line, width / 2, y);
    y += 60;
  }

  const qrSize = 560;
  const panel = qrPanelSize(qrSize);
  const quiet = Math.max(24, Math.round(qrSize * 0.1));
  const panelX = (width - panel) / 2;
  const panelY = Math.max(y + 16, 300);
  drawQrModules(ctx, options.qrImage, panelX + quiet, panelY + quiet, qrSize);

  drawUrlBlock(
    ctx,
    options,
    width / 2,
    panelY + panel + 56,
    width - 160,
    30,
    32,
    VINYL.ink,
    VINYL.muted
  );

  drawBrandLockup(ctx, options.brandMark, width / 2, height - 76, 40, 36, VINYL.ink);
}

function drawWindshield(ctx: CanvasRenderingContext2D, options: QrStickerOptions) {
  const { width, height } = QR_STICKER_TEMPLATES.windshield;
  ctx.fillStyle = VINYL.bg;
  ctx.fillRect(0, 0, width, height);
  drawAccentChrome(ctx, options.accentColor, width, height, 12, 'vinyl');

  ctx.strokeStyle = VINYL.hairline;
  ctx.lineWidth = 5;
  ctx.strokeRect(20, 20, width - 40, height - 40);

  const qrSize = 380;
  const panel = qrPanelSize(qrSize);
  const quiet = Math.max(24, Math.round(qrSize * 0.1));
  const panelX = width - panel - 60;
  const panelY = (height - panel) / 2;
  drawQrModules(ctx, options.qrImage, panelX + quiet, panelY + quiet, qrSize);

  const leftPad = 96;
  const textMax = panelX - leftPad - 60;
  const lockupInk = resolveAccent(options.accentColor, VINYL.ink);

  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  drawBiltMark(ctx, options.brandMark, leftPad, 56, 52, lockupInk);
  ctx.fillStyle = lockupInk;
  ctx.font = font(700, 44);
  ctx.fillText('BILT', leftPad + 64, 96);

  ctx.fillStyle = VINYL.ink;
  ctx.font = font(700, 78);
  const nameLines = wrapText(ctx, options.vehicleName, textMax, 2);
  let y = 220;
  for (const line of nameLines) {
    ctx.fillText(line, leftPad, y);
    y += 90;
  }

  drawUrlBlock(
    ctx,
    options,
    leftPad,
    height - 130,
    textMax,
    32,
    34,
    VINYL.ink,
    VINYL.muted,
    'left'
  );
}

function drawEventCard(ctx: CanvasRenderingContext2D, options: QrStickerOptions) {
  const { width, height } = QR_STICKER_TEMPLATES.event_card;
  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, width, height);

  let contentTop = 140;
  if (options.vehicleImage) {
    const imageHeight = 720;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, width, imageHeight);
    ctx.clip();
    drawImageCover(ctx, options.vehicleImage, 0, 0, width, imageHeight);
    ctx.restore();
    const scrim = ctx.createLinearGradient(0, imageHeight * 0.4, 0, imageHeight);
    scrim.addColorStop(0, 'rgba(9,9,11,0)');
    scrim.addColorStop(1, 'rgba(9,9,11,1)');
    ctx.fillStyle = scrim;
    ctx.fillRect(0, 0, width, imageHeight);
    contentTop = imageHeight + 20;
  }
  drawAccentChrome(ctx, options.accentColor, width, height, 18, 'display');

  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  drawBrandLockup(
    ctx,
    options.brandMark,
    width / 2,
    contentTop + (options.vehicleImage ? 20 : 80),
    56,
    48,
    resolveAccent(options.accentColor, COLORS.text)
  );

  ctx.fillStyle = COLORS.text;
  ctx.font = font(700, 84);
  const nameLines = wrapText(ctx, options.vehicleName, width - 240, 2);
  let y = contentTop + (options.vehicleImage ? 110 : 200);
  for (const line of nameLines) {
    ctx.fillText(line, width / 2, y);
    y += 98;
  }

  const qrSize = 620;
  const panel = qrPanelSize(qrSize);
  const qrY = y + 40;
  drawQrPanel(ctx, options.qrImage, (width - panel) / 2, qrY, qrSize);

  drawUrlBlock(
    ctx,
    options,
    width / 2,
    qrY + panel + 80,
    width - 240,
    40,
    42,
    COLORS.text,
    COLORS.muted
  );

  ctx.strokeStyle = COLORS.border;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(140, height - 170);
  ctx.lineTo(width - 140, height - 170);
  ctx.stroke();
  drawBrandLockup(ctx, options.brandMark, width / 2, height - 96, 40, 36, COLORS.dim);
}

function drawShowBoard(ctx: CanvasRenderingContext2D, options: QrStickerOptions) {
  const { width, height } = QR_STICKER_TEMPLATES.show_board;
  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, width, height);

  const qrSize = 1100;
  const panel = qrPanelSize(qrSize);
  const panelX = width - panel - 200;
  const panelY = (height - panel) / 2;
  const leftPad = 200;
  const textMax = panelX - leftPad - 120;

  if (options.vehicleImage) {
    const imageHeight = Math.round(height * 0.46);
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, width, imageHeight);
    ctx.clip();
    drawImageCover(ctx, options.vehicleImage, 0, 0, width, imageHeight);
    ctx.restore();
    const scrim = ctx.createLinearGradient(0, imageHeight * 0.3, 0, imageHeight);
    scrim.addColorStop(0, 'rgba(9,9,11,0.1)');
    scrim.addColorStop(1, 'rgba(9,9,11,1)');
    ctx.fillStyle = scrim;
    ctx.fillRect(0, 0, width, imageHeight);
    const sideScrim = ctx.createLinearGradient(panelX - 300, 0, panelX, 0);
    sideScrim.addColorStop(0, 'rgba(9,9,11,0)');
    sideScrim.addColorStop(1, 'rgba(9,9,11,0.75)');
    ctx.fillStyle = sideScrim;
    ctx.fillRect(panelX - 300, 0, width - (panelX - 300), imageHeight);
  }
  drawAccentChrome(ctx, options.accentColor, width, height, 24, 'display');

  drawQrPanel(ctx, options.qrImage, panelX, panelY, qrSize);

  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';

  const textTop = options.vehicleImage ? Math.round(height * 0.52) : 420;
  const lockupInk = resolveAccent(options.accentColor, COLORS.text);
  drawBiltMark(ctx, options.brandMark, leftPad, textTop - 20, 88, lockupInk);
  ctx.fillStyle = lockupInk;
  ctx.font = font(700, 72);
  ctx.fillText('BILT', leftPad + 108, textTop + 48);

  ctx.fillStyle = COLORS.text;
  ctx.font = font(800, 150);
  const nameLines = wrapText(ctx, options.vehicleName, textMax, 3);
  let y = textTop + 210;
  for (const line of nameLines) {
    ctx.fillText(line, leftPad, y);
    y += 172;
  }

  drawUrlBlock(
    ctx,
    options,
    leftPad,
    height - 300,
    textMax,
    60,
    64,
    COLORS.text,
    COLORS.muted,
    'left'
  );
}

/**
 * Renders a printable QR sticker to a PNG blob at 300 DPI-equivalent size.
 */
export async function renderQrSticker(options: QrStickerOptions): Promise<Blob> {
  const spec = QR_STICKER_TEMPLATES[options.template];
  const canvas = document.createElement('canvas');
  canvas.width = spec.width;
  canvas.height = spec.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is not supported in this browser');

  const opts: QrStickerOptions = {
    ...options,
    vehicleImage: spec.supportsVehicleImage ? options.vehicleImage : null,
  };

  switch (options.template) {
    case 'square_2in':
      drawSquare2In(ctx, opts);
      break;
    case 'square_sticker':
      drawSquareSticker(ctx, opts);
      break;
    case 'windshield':
      drawWindshield(ctx, opts);
      break;
    case 'event_card':
      drawEventCard(ctx, opts);
      break;
    case 'show_board':
      drawShowBoard(ctx, opts);
      break;
  }

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/png')
  );
  if (!blob) throw new Error('Failed to export the sticker image');
  return blob;
}
